'use client'

import clsx from 'clsx'
import Image from 'next/image'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { memo, useCallback, useEffect, useState } from 'react'

import { FeaturesRow } from '@/widgets/features-row'
import { Slider } from '@/widgets/slider'

import { sendYandexMetricaEvent } from '@/shared/integrations/yandex-metric/utils/send-metric-event'
import { BASE_URL } from '@/shared/lib/const/env'
import { useMediaQueries } from '@/shared/lib/hooks/useMediaQueries'
import { WEB_CONSEST_LINK_SC, WEB_OFFER_LINK_SC_NEW, WEB_POLICY_LINK_SC } from '@/shared/lib/utils/constants/pdfDocs'
import { ServiceCategories } from '@/shared/types/deprecated-types'
import { Text } from '@/shared/ui'
import { Modal } from '@/shared/ui'
import { CategoryCard } from '@/shared/ui/category-card'
import { ElementsSliderFree } from '@/shared/ui/elements-slider-free'
import { FeatureCard } from '@/shared/ui/feature-card'
import { Heading } from '@/shared/ui/heading'
import { HelperBlock } from '@/shared/ui/helper-block'
import { Hint } from '@/shared/ui/hint'
import { PdfList } from '@/shared/ui/PdfList'
import { PlasticCardPromotion } from '@/shared/ui/plastic-card-promotion'

import { SERVICE_GTM_IDS, SERVICE_YA_METRICS_IDS } from '../data/analitycs'
import { featureCards } from '../data/feature-cards'
import { featureRow } from '../data/feature-row'
import { Slide } from '../types/types'

import { ServiceForm } from './service-form/service-form'
import { SlideEl } from './slide-el/slide-el'

import cl from './service.module.scss'

type ServiceProps = {
	sliderImages?: Slide[]
	openFormModal?: () => void
	categories: ServiceCategories[]
}

const Service = memo((props: ServiceProps) => {
	const { sliderImages = [], openFormModal, categories } = props
	const { isMobile } = useMediaQueries()

	return (
		<>
			<Slider
				isNavigationHidden={isMobile}
				className={cl.slider}
				slides={sliderImages.map(slide => (
					<SlideEl
						key={slide.ID}
						name={slide.NAME}
						link={slide.LINK_VALUE}
						img={slide.IMG}
						imgMobile={slide.IMG_MOBILE}
					/>
				))}
			/>
			<section className={cl.section}>
				<Heading preset="h2" className={cl.title}>
					Обслуживание и ремонт
				</Heading>
				<ElementsSliderFree classNameRoot={cl.categoriesSlider} classNameSwiper={cl.categoriesSlider__swiper}>
					{categories
						?.filter(item => item.DEPTH_LEVEL === '1')
						?.map(category => (
							<CategoryCard
								key={category.NAME}
								name={category.NAME}
								link={'/service/details/' + category.CODE}
								image={BASE_URL + category.PICTURE}
							/>
						))}
				</ElementsSliderFree>
			</section>
			<section className={cl.section}>
				<Heading preset="h2" className={cl.title}>
					Другие популярные категории
				</Heading>
				<div className={cl.features}>
					{featureCards(openFormModal).map((feature, index) => (
						<FeatureCard
							key={index}
							className={cl.features__item}
							variant="horizontal"
							icon={feature.icon}
							heading={feature.heading}
							text={feature.text}
							actionLabel={feature.actionLabel}
							actionHref={feature.actionHref}
							actionOnClick={feature.actionOnClick}
							actionElementClasses={feature.actionElementClasses}
						/>
					))}
				</div>
			</section>
			<Hint
				className={clsx(cl.section, cl.section_hint)}
				hint={
					<>По&nbsp;вопросам обслуживания и&nbsp;ремонта вы&nbsp;можете обратиться в&nbsp;любой сервисный центр iPort</>
				}
				actionLabel="Адреса сервисных центров"
				actionHref="/shops/?show=services"
			/>

			<section className={clsx(cl.section, cl.plasticCards)}>
				<PlasticCardPromotion variant="iport-plus-service" />
				<PlasticCardPromotion variant="nbclub" />
				<PlasticCardPromotion variant="for-business" />
			</section>
			<Link prefetch={false} href={'/service/about-us'}>
				<section className={clsx(cl.section, cl.promo)}>
					<Image
						src={'https://storage.yandexcloud.net/tf-bitrix-upload/iport/promo/promo-service/promo_service.png'}
						alt={''}
						width={1420}
						height={600}
						className={cl.img}
					/>
					<div className={cl.footer}>
						<div className={cl.wrapper}>
							<h2 className={cl.title}>Сервис, которому доверяют</h2>
							<p className={cl.subtitle}>Узнайте о нас больше</p>
						</div>
						<span className={cl.btn}>Подробнее</span>
					</div>
				</section>
			</Link>
			<section className={cl.section}>
				<FeaturesRow features={featureRow} />
			</section>
			<section className={cl.section}>
				<HelperBlock
					heading={
						<>
							Не&nbsp;можете разобраться, что сломалось? <br />
							Свяжитесь с&nbsp;нами и&nbsp;мы&nbsp;поможем!
						</>
					}
					actionLabel="Оставить заявку"
					actionOnClick={openFormModal}
					actionElementClasses={[SERVICE_GTM_IDS.helperBlockAction, SERVICE_YA_METRICS_IDS.helperBlockAction]}
					secondaryText={
						<>
							<Text as="a" size="7" href="tel:+78005555761" className={cl.helperBlock__link}>
								+7 (800) 555-57-61
							</Text>
							<Text className={cl.helperBlock__workingHours}>10:00&nbsp;&mdash; 22:00, ежедневно</Text>
						</>
					}
				/>
			</section>
			<PdfList
				items={[
					{
						title: 'Политика конфиденциальности',
						link: WEB_POLICY_LINK_SC
					},
					{ title: 'Согласие на обработку персональных данных', link: WEB_CONSEST_LINK_SC },
					{ title: 'Оферта', link: WEB_OFFER_LINK_SC_NEW }
				]}
			/>
		</>
	)
})

export type ServicePageProps = Omit<ServiceProps, 'openFormModal'>

export const ServicePage = (props: ServicePageProps) => {
	const { sliderImages = [], categories } = props

	const searchParams = useSearchParams()
	const openFormOnLoad = searchParams.has('openmodal')

	const [openFormModal, setOpenFormModal] = useState(false)

	const setOpen = useCallback(() => setOpenFormModal(true), [])
	const setClose = () => setOpenFormModal(false)

	useEffect(() => {
		openFormOnLoad ? setOpen() : null
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const onOpenFormModalHandler = () => {
		setOpen()
		sendYandexMetricaEvent(`init_service_request`)
	}

	return (
		<>
			<Service sliderImages={sliderImages} openFormModal={onOpenFormModalHandler} categories={categories} />
			<Modal isOpen={openFormModal} onClose={setClose} contentClassName={cl.modal}>
				<ServiceForm />
			</Modal>
		</>
	)
}
